import { useLazyQuery } from '@apollo/client'
import { Download, Favorite } from '@mui/icons-material'
import LinkIcon from '@mui/icons-material/Link'
import { Box, Button, Link, Typography, useTheme } from '@mui/material'
import { GridRenderCellParams, type GridColDef } from '@mui/x-data-grid-pro'
import {
    RosterColors,
    SearchFacetInput,
    SortIndexField,
    type SystemStatus,
} from '__generated__/graphql'
import { downloadDocument } from 'candidate/graphql/profile.gql'
import { formatDate } from 'date-fns'
import { FC, useMemo } from 'react'
import { NameColumnHeader } from 'client/candidate-table-temp/components/RosterCandidatesTable/NameColumnHeader'
import RecruitingStatusPill from 'client/candidate-table-temp/components/RecruitingStatusPill'
import RosterCandidateNameCell from 'client/candidate-table-temp/components/RosterCandidateNameCell'
import SystemStatusPill from 'client/candidate-table-temp/components/SystemStatusPill'
import { GetCandidatesForClientPortalSql } from 'client/candidate-table-temp/graphql/candidateSearch.gql'
import ColorsAvatarGroup from 'recruiter/pages/Rosters/RosterProfile/CandidatesTableColors/ColorsAvatarGroup'
import ColorsDropdown from 'recruiter/pages/Rosters/RosterProfile/CandidatesTableColors/ColorsDropdown'
import { recruiterRouteConfig } from 'recruiter/routes'
import { isCandidateRecruitingStatus } from 'shared/models/candidateRecruitingStatus'
import useToast from 'shared/components/Toast/useToast'
import { type RosterCandidateTableRowModel } from 'client/candidate-table-temp/hooks/useRosterSearchWithFilters'

type RosterCandidateTableColumnDefinition =
    GridColDef<RosterCandidateTableRowModel> & {
        sortIndexField?: SortIndexField
    }

interface UseSharedColumnDefsParams {
    canEdit: boolean
    ActionsCell: FC<GridRenderCellParams>
    setActiveFilters: (filters: SearchFacetInput[]) => void
}

const valueGetter = (value: any) => value ?? '-'

export function useSharedColumnDefs({
    canEdit,
    ActionsCell,
    setActiveFilters,
}: UseSharedColumnDefsParams): Record<
    string,
    RosterCandidateTableColumnDefinition
> {
    const theme = useTheme()
    const toast = useToast()
    const [download] = useLazyQuery(downloadDocument, {
        fetchPolicy: 'network-only',
    })

    return useMemo(
        () => ({
            rowNumber: {
                field: 'rowNumber',
                headerName: '#',
                sortable: false,
                editable: false,
                width: 30,
                align: 'center',
                headerAlign: 'center',
                valueGetter: (_value, row, _column, apiRef) => {
                    if (!apiRef.current) return ''
                    const index =
                        apiRef.current.getRowIndexRelativeToVisibleRows(
                            apiRef.current.getRowId(row)
                        )
                    if (isNaN(index)) return ''

                    return index + 1
                },
            },
            name: {
                field: 'name',
                editable: false,
                sortable: false,
                width: 220,
                renderCell: ({ row }) => (
                    <RosterCandidateNameCell
                        row={row}
                        setActiveFilters={() => setActiveFilters([])}
                    />
                ),
                renderHeader: () => <NameColumnHeader />,
            },
            colors: {
                field: 'colors',
                headerName: 'Colors',
                sortable: false,
                editable: canEdit ?? false,
                width: 65,
                renderCell: ({ id, row }) =>
                    row.rosterCandidate ? (
                        <ColorsAvatarGroup
                            colors={
                                (row.rosterCandidate?.candidateColors
                                    ?.map(({ rosterColor }) => rosterColor)
                                    .filter((c) => !!c) ?? []) as RosterColors[]
                            }
                            id={id.toString()}
                            disabled={!canEdit}
                        />
                    ) : (
                        '-'
                    ),
                renderEditCell: (params) => {
                    return params.row.rosterCandidate ? (
                        <ColorsDropdown
                            colors={
                                (params.row.rosterCandidate?.candidateColors
                                    ?.map(({ rosterColor }) => rosterColor)
                                    .filter((c) => !!c) ?? []) as RosterColors[]
                            }
                            rosterCandidateId={
                                params.row.rosterCandidateId || ''
                            }
                            refetchQueries={[GetCandidatesForClientPortalSql]}
                            canEdit={canEdit ?? false}
                            {...params}
                        />
                    ) : (
                        '-'
                    )
                },
            },
            rosterCandidateFavoriteCount: {
                field: 'rosterCandidateFavoriteCount',
                headerName: 'Favorited',
                editable: false,
                sortable: false,
                width: 80,
                valueGetter: (value, row) => row?.rosterCandidateFavoriteCount,
                renderCell: ({ row }) =>
                    (row.rosterCandidateFavoriteCount || 0) > 0 && (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            height="100%"
                        >
                            <Favorite fontSize="large" color="error" />
                            <Typography
                                variant="caption"
                                position="absolute"
                                fontWeight="bold"
                                color={theme.palette.common.white}
                            >
                                {row.rosterCandidateFavoriteCount}
                            </Typography>
                        </Box>
                    ),
            },
            actions: {
                field: 'actions',
                headerName: '',
                sortable: false,
                editable: false,
                width: 100,
                renderCell: ActionsCell,
                renderHeader: () => null,
                resizable: false,
            },
            dateAdded: {
                field: 'dateAdded',
                headerName: 'Date Added',
                editable: false,
                sortable: true,
                sortIndexField: SortIndexField.DateOnRoster,
                width: 120,
                valueGetter: (value, row) =>
                    row.rosterCandidateCreatedAt
                        ? formatDate(row.rosterCandidateCreatedAt, 'MM/dd/yyyy')
                        : '-',
            },
            processStatusUpdatedAt: {
                field: 'processStatusUpdatedAt',
                headerName: 'Process Status Updated At',
                editable: false,
                sortable: true,
                width: 180,
                sortIndexField: SortIndexField.ProcessStatusUpdatedAt,
                valueGetter: (value, row) =>
                    row.rosterCandidate?.candidateProcessStatus?.createdAt
                        ? formatDate(
                              row.rosterCandidate.candidateProcessStatus
                                  .createdAt,
                              'MM/dd/yyyy'
                          )
                        : '-',
            },
            act: {
                field: 'act',
                valueGetter,
                sortable: false,
                editable: false,
            },
            analystClassYear: {
                field: 'analystClassYear',
                valueGetter,
                sortable: false,
                editable: false,
            },
            candidateProfileUrl: {
                field: 'candidateProfileUrl',
                sortable: false,
                editable: false,
                renderCell: ({ row }) => (
                    <Button
                        onClick={() => {
                            setActiveFilters([])
                        }}
                        href={`${recruiterRouteConfig.candidates.path}/${row.candidateId}`}
                        aria-label="View Candidate Profile"
                        variant="text"
                    >
                        <LinkIcon />
                    </Button>
                ),
            },
            cpiPlaced: {
                field: 'cpiPlaced',
                sortable: false,
                editable: false,
                valueGetter: (value, row) => row?.cpiAttribute ?? '-',
            },
            currentCompany: {
                field: 'company',
                headerName: 'Company',
                editable: false,
                sortable: false,
                width: 150,
                renderCell: ({ row }) =>
                    row.currentCompany ? (
                        <Link
                            onClick={() => {
                                setActiveFilters([])
                            }}
                            href={`/companies/${row.currentCompanyId}`}
                            sx={{ textDecoration: 'none' }}
                        >
                            {row.currentCompany}
                        </Link>
                    ) : (
                        '-'
                    ),
            },
            ccatRawScore: {
                field: 'ccatRawScore',
                headerName: 'CCAT Raw Score',
                editable: false,
                sortable: false,
                width: 150,
                valueGetter,
            },
            ccatPercentile: {
                field: 'ccatPercentile',
                headerName: 'CCAT Percentile',
                editable: false,
                sortable: false,
                width: 150,
                valueGetter,
            },
            currentIndustry: {
                field: 'industries',
                headerName: 'Industries',
                editable: false,
                sortable: false,
                width: 150,
                renderCell: ({ row }) => {
                    const industryString =
                        row.currentIndustry?.split(';').join(', ') || '-'

                    return (
                        <Box component="span" title={industryString}>
                            {industryString}
                        </Box>
                    )
                },
            },
            publicBio: {
                field: 'publicBio',
                headerName: 'Public Bio',
                editable: false,
                sortable: false,
                width: 150,
                valueGetter,
            },
            currentLocation: { field: 'currentLocation', valueGetter },
            currentSector: {
                field: 'sectors',
                headerName: 'Sectors',
                editable: false,
                sortable: false,
                width: 150,
                renderCell: ({ row }) => {
                    const sectorString =
                        row.currentSector?.split(';').join(', ') || '-'

                    return (
                        <Box component="span" title={sectorString}>
                            {sectorString}
                        </Box>
                    )
                },
            },
            currentSectorDescription: {
                field: 'currentSectorDescription',
                valueGetter,
                sortable: false,
                editable: false,
            },
            currentTitle: {
                field: 'currentTitle',
                valueGetter,
                sortable: false,
                editable: false,
            },
            email: {
                field: 'email',
                valueGetter,
                sortable: false,
                editable: false,
            },
            ethnicity: {
                field: 'ethnicity',
                valueGetter,
                sortable: false,
                editable: false,
            },
            gender: {
                field: 'gender',
                valueGetter,
                sortable: false,
                editable: false,
            },
            gmat: {
                field: 'gmat',
                valueGetter,
                sortable: false,
                editable: false,
            },
            hometown: {
                field: 'hometown',
                valueGetter,
                sortable: false,
                editable: false,
            },
            linkedinUrl: {
                field: 'linkedinUrl',
                sortable: false,
                editable: false,
                renderCell: ({ row }) =>
                    row.linkedinUrl ? (
                        <Link href={`//${row.linkedinUrl}`}>
                            {row.linkedinUrl}
                        </Link>
                    ) : (
                        '-'
                    ),
            },
            mbaEducation: {
                field: 'mbaEducation',
                valueGetter,
                sortable: false,
                editable: false,
            },
            mbaTiering: {
                field: 'mbaTiering',
                valueGetter,
                sortable: false,
                editable: false,
            },
            mbaYear: {
                field: 'mbaYear',
                valueGetter,
                sortable: false,
                editable: false,
            },
            noteForClient: {
                field: 'noteForClient',
                valueGetter,
                sortable: false,
                editable: false,
            },
            phone: {
                field: 'phone',
                valueGetter,
                sortable: false,
                editable: false,
            },
            preAdmitAttribute: {
                field: 'preAdmitAttribute',
                sortable: false,
                editable: false,
                valueGetter: (value, row) =>
                    row?.preAdmitAttribute ? 'Yes' : 'No',
            },
            preferredEmail: {
                field: 'preferredEmail',
                valueGetter,
                sortable: false,
                editable: false,
            },
            previousCompany: {
                field: 'previousCompany',
                valueGetter,
                sortable: false,
                editable: false,
            },
            previousIndustry: {
                field: 'previousIndustry',
                valueGetter,
                sortable: false,
                editable: false,
            },
            previousSector: {
                field: 'previousSector',
                valueGetter,
                sortable: false,
                editable: false,
            },
            previousStartDate: {
                field: 'previousStartDate',
                valueGetter,
                sortable: false,
                editable: false,
            },
            previousTitle: {
                field: 'previousTitle',
                valueGetter,
                sortable: false,
                editable: false,
            },
            recruitingGeoPref1: {
                field: 'recruitingGeoPref1',
                sortable: false,
                editable: false,
                valueGetter: (value, row) =>
                    row.recruitingGeoLocationPref1 || '-',
            },
            recruitingGeoPref2: {
                field: 'recruitingGeoPref2',
                sortable: false,
                editable: false,
                valueGetter: (value, row) =>
                    row.recruitingGeoLocationPref2 || '-',
            },
            recruitingGeoPref3: {
                field: 'recruitingGeoPref3',
                sortable: false,
                editable: false,
                valueGetter: (value, row) =>
                    row.recruitingGeoLocationPref3 || '-',
            },
            recruitingIndustryPref1: {
                field: 'recruitingIndustryPref1',
                sortable: false,
                editable: false,
                valueGetter,
            },
            recruitingIndustryPref2: {
                field: 'recruitingIndustryPref2',
                sortable: false,
                editable: false,
                valueGetter,
            },
            recruitingSectorPref1: {
                field: 'recruitingSectorPref1',
                sortable: false,
                editable: false,
                valueGetter,
            },
            recruitingSectorPref2: {
                field: 'recruitingSectorPref2',
                sortable: false,
                editable: false,
                valueGetter,
            },
            recruitingSectorPref3: {
                field: 'recruitingSectorPref3',
                sortable: false,
                editable: false,
                valueGetter,
            },
            recruitingStatus: {
                field: 'recruitingStatus',
                sortable: false,
                editable: false,
                headerName: 'Recruiting Status',
                renderCell: ({ row }) => (
                    <RecruitingStatusPill
                        status={
                            row.recruitingStatus &&
                            isCandidateRecruitingStatus(row.recruitingStatus)
                                ? row.recruitingStatus
                                : 'No Status'
                        }
                    />
                ),
                width: 150,
            },
            referenceAttribute: {
                field: 'referenceAttribute',
                valueGetter,
                sortable: false,
                editable: false,
            },
            resumeUpdatedDate: {
                field: 'resumeUpdatedDate',
                sortable: false,
                editable: false,
                valueGetter: (value, row) =>
                    value ? formatDate(new Date(value), 'MM/dd/yyyy') : '-',
            },
            resumeUrl: {
                field: 'candidateDocumentId',
                sortable: false,
                editable: false,
                renderCell: ({ row }) =>
                    row.candidateDocumentId ? (
                        <Button
                            variant="text"
                            onClick={async () => {
                                const { data } = await download({
                                    variables: {
                                        documentId: row.candidateDocumentId,
                                    },
                                })
                                if (data.download.url) {
                                    const hiddenDownloadLink =
                                        document.createElement('a')
                                    hiddenDownloadLink.href = data.download.url
                                    hiddenDownloadLink.target = '_blank'
                                    hiddenDownloadLink.click()
                                    hiddenDownloadLink.remove()
                                } else {
                                    toast({
                                        title: 'Something went wrong',
                                        message: 'Please try again later.',
                                    })
                                }
                            }}
                        >
                            <Download />
                        </Button>
                    ) : (
                        '-'
                    ),
            },
            rosterCommentsFromBlast: {
                field: 'rosterCommentsFromBlast',
                sortable: false,
                editable: false,
                valueGetter,
            },
            rosterInterestFromBlast: {
                field: 'rosterInterestFromBlast',
                sortable: false,
                editable: false,
                valueGetter: (value: boolean) =>
                    value === true ? 'Yes' : value === false ? 'No' : '-',
            },
            rosterNotInterestedReasonFromBlast: {
                field: 'rosterNotInterestedReasonFromBlast',
                sortable: false,
                editable: false,
                valueGetter,
            },
            rosterLocationFromBlast: {
                field: 'rosterLocationFromBlast',
                sortable: false,
                editable: false,
                valueGetter: (value, row) =>
                    row?.rosterCandidate?.mostRecentJobBoardResponse?.officePrefs?.map(
                        (op) => op.companyOffice?.officeLocation?.value
                    ) ?? '-',
            },
            rosterSectorFromBlast: {
                field: 'rosterSectorFromBlast',
                sortable: false,
                editable: false,
                valueGetter: (value, row) =>
                    row?.rosterCandidate?.mostRecentJobBoardResponse?.sectorPrefs?.map(
                        (sp) => sp.sector?.value
                    ) ?? '-',
            },
            sat: {
                field: 'sat',
                valueGetter,
                sortable: false,
                editable: false,
            },
            startDate: {
                field: 'startDate',
                valueGetter,
                sortable: false,
                editable: false,
            },
            systemStatus: {
                field: 'systemStatus',
                headerName: 'Registration Status',
                editable: false,
                sortable: false,
                width: 150,
                renderCell: ({ row }) => (
                    <SystemStatusPill
                        status={row.systemStatus as SystemStatus}
                        statusDate={row.systemStatusDate}
                    />
                ),
            },
            undergradEducation: {
                field: 'college',
                headerName: 'Undergrad College',
                editable: false,
                sortable: false,
                width: 150,
                valueGetter: (value, row) => row.undergradEducation || '-',
            },
            undergradGpa: {
                field: 'undergradGpa',
                valueGetter,
                sortable: false,
                editable: false,
            },
            undergradYear: {
                field: 'undergradYear',
                valueGetter,
                sortable: false,
                editable: false,
            },
            visaStatus: {
                field: 'visaStatus',
                valueGetter,
                sortable: false,
                editable: false,
            },
        }),
        [canEdit, ActionsCell, setActiveFilters, theme, download, toast]
    )
}
